import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  MapPin, 
  Calendar as CalendarNav, 
  Music, 
  Wine, 
  Star, 
  Building,
  Anchor,
  Store,
  MessageCircle,
  ChevronDown,
  Clock,
  Sparkles,
  Heart,
  Users,
  Sun,
  Church,
  ExternalLink,
  Bookmark,
  BookmarkCheck,
  Navigation,
  Filter,
  X,
  Search,
  Loader2,
  ThumbsUp,
  MessageSquare
} from "lucide-react";
import { Footer } from "../components/layout/Footer";
import { useLanguage } from "../context/LanguageContext";
import { useLocation } from "../context/LocationContext";
import { API } from "../lib/api";

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [topPicks, setTopPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTimeframe, setSelectedTimeframe] = useState("upcoming");
  const [showAll, setShowAll] = useState(false);
  const [savedEvents, setSavedEvents] = useState([]);
  const [showAskAI, setShowAskAI] = useState(false);
  const [aiQuery, setAiQuery] = useState("");
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    freeOnly: false,
    familyFriendly: false,
    maxDistance: 30
  });
  
  const { language } = useLanguage();
  const { location, locationData } = useLocation();

  const translations = {
    en: {
      title: "What's On",
      subtitle: "Events & experiences near",
      all: "All",
      festivals: "Festivals",
      music: "Music",
      foodWine: "Food & Wine",
      markets: "Markets",
      cultural: "Cultural",
      beach: "Beach & Parties",
      family: "Family",
      local: "Local & Authentic",
      noEvents: "No events found for this period",
      contact: "Contact on WhatsApp",
      showMore: "Show more events",
      showLess: "Show less",
      free: "Free",
      verified: "Verified",
      today: "Today",
      thisWeek: "This Week",
      upcoming: "Upcoming",
      happeningNow: "Happening now",
      startsAt: "Starts at",
      eventsCount: "events",
      topPicks: "Top Picks",
      topPicksSubtitle: "Don't miss these unique experiences",
      askAI: "Ask AI",
      aiPlaceholder: "What's happening tonight near me?",
      aiSuggestions: "AI Suggestions",
      bookNow: "Book Now",
      getDirections: "Directions",
      saveEvent: "Save",
      saved: "Saved",
      partner: "Partner",
      filters: "Filters",
      freeOnly: "Free only",
      familyFriendly: "Family friendly",
      clearFilters: "Clear filters",
      recurring: "Recurring"
    },
    it: {
      title: "Cosa c'è",
      subtitle: "Eventi ed esperienze vicino a",
      all: "Tutti",
      festivals: "Festival",
      music: "Musica",
      foodWine: "Cibo e Vino",
      markets: "Mercati",
      cultural: "Culturale",
      beach: "Spiaggia e Feste",
      family: "Famiglie",
      local: "Locale e Autentico",
      noEvents: "Nessun evento trovato per questo periodo",
      contact: "Contatta su WhatsApp",
      showMore: "Mostra altri eventi",
      showLess: "Mostra meno",
      free: "Gratuito",
      verified: "Verificato",
      today: "Oggi",
      thisWeek: "Questa Settimana",
      upcoming: "Prossimi",
      happeningNow: "In corso",
      startsAt: "Inizia alle",
      eventsCount: "eventi",
      topPicks: "Top Picks",
      topPicksSubtitle: "Non perdere queste esperienze uniche",
      askAI: "Chiedi all'AI",
      aiPlaceholder: "Cosa succede stasera vicino a me?",
      aiSuggestions: "Suggerimenti AI",
      bookNow: "Prenota",
      getDirections: "Indicazioni",
      saveEvent: "Salva",
      saved: "Salvato",
      partner: "Partner",
      filters: "Filtri",
      freeOnly: "Solo gratuiti",
      familyFriendly: "Per famiglie",
      clearFilters: "Cancella filtri",
      recurring: "Ricorrente"
    },
    fr: {
      title: "Quoi de neuf",
      subtitle: "Événements et expériences près de",
      all: "Tous",
      festivals: "Festivals",
      music: "Musique",
      foodWine: "Gastronomie",
      markets: "Marchés",
      cultural: "Culturel",
      beach: "Plage & Fêtes",
      family: "Famille",
      local: "Local & Authentique",
      noEvents: "Aucun événement trouvé",
      contact: "Contacter sur WhatsApp",
      showMore: "Plus d'événements",
      showLess: "Afficher moins",
      free: "Gratuit",
      verified: "Vérifié",
      today: "Aujourd'hui",
      thisWeek: "Cette Semaine",
      upcoming: "À venir",
      happeningNow: "En cours",
      startsAt: "Commence à",
      eventsCount: "événements",
      topPicks: "Coups de Cœur",
      topPicksSubtitle: "Ne manquez pas ces expériences uniques",
      askAI: "Demander à l'IA",
      aiPlaceholder: "Que se passe-t-il ce soir près de moi?",
      aiSuggestions: "Suggestions IA",
      bookNow: "Réserver",
      getDirections: "Itinéraire",
      saveEvent: "Sauvegarder",
      saved: "Sauvegardé",
      partner: "Partenaire",
      filters: "Filtres",
      freeOnly: "Gratuit uniquement",
      familyFriendly: "Familial",
      clearFilters: "Effacer",
      recurring: "Récurrent"
    },
    de: {
      title: "Was ist los",
      subtitle: "Events und Erlebnisse in der Nähe von",
      all: "Alle",
      festivals: "Festivals",
      music: "Musik",
      foodWine: "Essen & Wein",
      markets: "Märkte",
      cultural: "Kulturell",
      beach: "Strand & Partys",
      family: "Familie",
      local: "Lokal & Authentisch",
      noEvents: "Keine Events gefunden",
      contact: "Auf WhatsApp kontaktieren",
      showMore: "Mehr Events",
      showLess: "Weniger",
      free: "Kostenlos",
      verified: "Verifiziert",
      today: "Heute",
      thisWeek: "Diese Woche",
      upcoming: "Kommende",
      happeningNow: "Läuft jetzt",
      startsAt: "Beginnt um",
      eventsCount: "Events",
      topPicks: "Top-Empfehlungen",
      topPicksSubtitle: "Verpassen Sie diese einzigartigen Erlebnisse nicht",
      askAI: "KI fragen",
      aiPlaceholder: "Was passiert heute Abend in meiner Nähe?",
      aiSuggestions: "KI-Vorschläge",
      bookNow: "Buchen",
      getDirections: "Wegbeschreibung",
      saveEvent: "Speichern",
      saved: "Gespeichert",
      partner: "Partner",
      filters: "Filter",
      freeOnly: "Nur kostenlos",
      familyFriendly: "Familienfreundlich",
      clearFilters: "Filter löschen",
      recurring: "Wiederkehrend"
    },
    es: {
      title: "Qué hay",
      subtitle: "Eventos y experiencias cerca de",
      all: "Todos",
      festivals: "Festivales",
      music: "Música",
      foodWine: "Gastronomía",
      markets: "Mercados",
      cultural: "Cultural",
      beach: "Playa y Fiestas",
      family: "Familia",
      local: "Local y Auténtico",
      noEvents: "No se encontraron eventos",
      contact: "Contactar en WhatsApp",
      showMore: "Más eventos",
      showLess: "Menos",
      free: "Gratis",
      verified: "Verificado",
      today: "Hoy",
      thisWeek: "Esta Semana",
      upcoming: "Próximos",
      happeningNow: "Ahora mismo",
      startsAt: "Empieza a las",
      eventsCount: "eventos",
      topPicks: "Recomendados",
      topPicksSubtitle: "No te pierdas estas experiencias únicas",
      askAI: "Preguntar a la IA",
      aiPlaceholder: "¿Qué pasa esta noche cerca de mí?",
      aiSuggestions: "Sugerencias de IA",
      bookNow: "Reservar",
      getDirections: "Indicaciones",
      saveEvent: "Guardar",
      saved: "Guardado",
      partner: "Socio",
      filters: "Filtros",
      freeOnly: "Solo gratis",
      familyFriendly: "Familiar",
      clearFilters: "Borrar filtros",
      recurring: "Recurrente"
    }
  };

  const txt = translations[language] || translations.en;

  const timeframes = [
    { id: "today", label: txt.today, icon: Clock },
    { id: "this_week", label: txt.thisWeek, icon: CalendarNav },
    { id: "upcoming", label: txt.upcoming, icon: Sparkles }
  ];

  const categories = [
    { id: "all", label: txt.all, icon: Star },
    { id: "food_wine", label: txt.foodWine, icon: Wine },
    { id: "beach_party", label: txt.beach, icon: Sun },
    { id: "music", label: txt.music, icon: Music },
    { id: "cultural", label: txt.cultural, icon: Building },
    { id: "family", label: txt.family, icon: Users },
    { id: "local_authentic", label: txt.local, icon: Church },
    { id: "market", label: txt.markets, icon: Store }
  ];

  // Load saved events from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('savedEvents');
    if (saved) {
      setSavedEvents(JSON.parse(saved));
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        location: location || 'monopoli',
        timeframe: selectedTimeframe,
        max_distance: filters.maxDistance
      });
      
      if (selectedCategory !== "all") {
        params.append("category", selectedCategory);
      }
      if (filters.freeOnly) {
        params.append("free_only", "true");
      }
      if (filters.familyFriendly) {
        params.append("family_friendly", "true");
      }
      
      const res = await axios.get(`${API}/events/enhanced?${params}`);
      setEvents(res.data);
    } catch (error) {
      console.error("Error fetching events:", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTimeframe, selectedCategory, location, filters]);

  const fetchTopPicks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/top-picks?location=${location || 'monopoli'}`);
      setTopPicks(res.data);
    } catch (error) {
      console.error("Error fetching top picks:", error);
    }
  }, [location]);

  useEffect(() => {
    fetchEvents();
    fetchTopPicks();
  }, [fetchEvents, fetchTopPicks]);

  const handleAskAI = async () => {
    if (!aiQuery.trim()) return;
    
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/events/ai-suggest?location=${location || 'monopoli'}`, {
        query: aiQuery
      });
      setAiResponse(res.data);
    } catch (error) {
      console.error("AI suggestion error:", error);
      setAiResponse({
        suggestions: "Sorry, I couldn't get suggestions right now. Please try again!",
        events: []
      });
    } finally {
      setAiLoading(false);
    }
  };

  const toggleSaveEvent = (eventId) => {
    let newSaved;
    if (savedEvents.includes(eventId)) {
      newSaved = savedEvents.filter(id => id !== eventId);
    } else {
      newSaved = [...savedEvents, eventId];
    }
    setSavedEvents(newSaved);
    localStorage.setItem('savedEvents', JSON.stringify(newSaved));
  };

  const getEventIcon = (category) => {
    const icons = { 
      "music": Music, 
      "beach_party": Sun,
      "market": Store,
      "food_wine": Wine,
      "festival": Star,
      "cultural": Building,
      "family": Users,
      "local_authentic": Church
    };
    return icons[category] || CalendarNav;
  };

  const getCategoryColor = (category) => {
    const colors = {
      "food_wine": "text-amber-600 bg-amber-50",
      "beach_party": "text-cyan-600 bg-cyan-50",
      "music": "text-red-600 bg-red-50",
      "cultural": "text-purple-600 bg-purple-50",
      "family": "text-pink-600 bg-pink-50",
      "local_authentic": "text-emerald-600 bg-emerald-50",
      "market": "text-blue-600 bg-blue-50",
      "festival": "text-yellow-600 bg-yellow-50"
    };
    return colors[category] || "text-stone-600 bg-stone-50";
  };

  const formatEventDate = (event) => {
    if (event.recurring === "weekly") return `Every ${event.recurring_day}`;
    if (event.recurring === "daily") return "Every day";
    const startDate = new Date(event.start_date);
    const options = { month: 'short', day: 'numeric' };
    if (event.end_date && event.end_date !== event.start_date) {
      const endDate = new Date(event.end_date);
      return `${startDate.toLocaleDateString('en-GB', options)} - ${endDate.toLocaleDateString('en-GB', options)}`;
    }
    return startDate.toLocaleDateString('en-GB', { ...options, weekday: 'short' });
  };

  const isHappeningNow = (event) => {
    const today = new Date().toISOString().split('T')[0];
    const start = event.start_date;
    const end = event.end_date || start;
    return start <= today && today <= end;
  };

  const handleWhatsApp = (event) => {
    const message = encodeURIComponent(`Hi! I'm interested in ${event.event_name}. Can you help me with more info?`);
    window.open(`https://wa.me/${locationData?.whatsapp || '393331234567'}?text=${message}`, '_blank');
  };

  const displayEvents = showAll ? events : events.slice(0, 6);

  return (
    <div className="min-h-screen bg-sand-200" data-testid="events-page">
      {/* Header */}
      <header className="bg-gradient-to-br from-sand-300 via-sand-200 to-terracotta-100 text-stone-900 px-6 pt-8 pb-6 border-b border-sand-300">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-serif text-stone-800">{txt.title}</h1>
          <p className="text-stone-500 text-sm mt-2">{txt.subtitle} {locationData?.name}</p>
          
          {/* Ask AI Button */}
          <button
            onClick={() => setShowAskAI(true)}
            className="mt-4 flex items-center gap-2 bg-olive-600 hover:bg-olive-700 text-white px-4 py-2.5 rounded-xl font-medium transition-colors shadow-md shadow-olive-600/20"
            data-testid="ask-ai-btn"
          >
            <MessageSquare size={18} />
            {txt.askAI}
          </button>
        </div>
      </header>

      {/* Top Picks Section */}
      {topPicks.length > 0 && (
        <section className="px-6 py-6 bg-gradient-to-b from-sand-100 to-sand-200 border-b border-sand-300">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-2">
              <ThumbsUp size={20} className="text-terracotta-600" />
              <h2 className="text-xl font-bold text-stone-800">{txt.topPicks}</h2>
            </div>
            <p className="text-stone-500 text-sm mb-4">{txt.topPicksSubtitle}</p>
            
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2 scrollbar-hide">
              {topPicks.map((event) => {
                const Icon = getEventIcon(event.category);
                return (
                  <div 
                    key={event.id}
                    className="flex-shrink-0 w-72 bg-white rounded-2xl overflow-hidden shadow-sm border border-sand-300 hover:shadow-lg hover:shadow-olive-600/10 transition-shadow"
                    data-testid="top-pick-card"
                  >
                    {event.image_url && (
                      <div className="h-36 relative">
                        <img 
                          src={event.image_url} 
                          alt={event.event_name}
                          className="w-full h-full object-cover"
                        />
                        {event.partner_event && (
                          <span className="absolute top-2 right-2 bg-terracotta-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                            {txt.partner}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="p-4">
                      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(event.category)}`}>
                        <Icon size={12} />
                        {event.category?.replace('_', ' ')}
                      </div>
                      <h3 className="font-semibold text-stone-800 mt-2 line-clamp-1">{event.event_name}</h3>
                      <p className="text-olive-700 text-sm mt-1">{formatEventDate(event)}</p>
                      <p className="text-stone-500 text-sm flex items-center gap-1 mt-1">
                        <MapPin size={12} /> {event.town} · {event.distance_km}km
                      </p>
                      
                      <div className="flex gap-2 mt-3">
                        {event.booking_url && (
                          <a
                            href={event.booking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 bg-olive-600 hover:bg-olive-700 text-white py-2 rounded-lg text-sm font-medium text-center transition-colors"
                          >
                            {txt.bookNow}
                          </a>
                        )}
                        <button
                          onClick={() => toggleSaveEvent(event.id)}
                          className={`p-2 rounded-lg border transition-colors ${
                            savedEvents.includes(event.id) 
                              ? 'bg-terracotta-50 border-terracotta-200 text-terracotta-600' 
                              : 'border-sand-300 text-stone-400 hover:text-terracotta-600'
                          }`}
                        >
                          {savedEvents.includes(event.id) ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Timeframe Tabs */}
      <div className="px-6 py-4 max-w-4xl mx-auto border-b border-sand-300 bg-sand-100/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex gap-1">
          {timeframes.map((tf) => {
            const Icon = tf.icon;
            return (
              <button
                key={tf.id}
                onClick={() => {
                  setSelectedTimeframe(tf.id);
                  setShowAll(false);
                }}
                data-testid={`timeframe-${tf.id}`}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all ${
                  selectedTimeframe === tf.id 
                    ? "bg-olive-600 text-white shadow-md shadow-olive-600/20" 
                    : "bg-white text-stone-600 hover:bg-sand-200 border border-sand-300"
                }`}
              >
                <Icon size={16} />
                {tf.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Category Filters */}
      <div className="px-6 py-4 max-w-4xl mx-auto bg-sand-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 flex-1">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                    selectedCategory === cat.id 
                      ? "bg-sea-700 text-white" 
                      : "bg-white border border-sand-300 text-stone-600 hover:border-olive-400"
                  }`}
                >
                  <Icon size={14} />
                  {cat.label}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`ml-2 p-2 rounded-lg border transition-colors flex-shrink-0 ${
              showFilters || filters.freeOnly || filters.familyFriendly
                ? 'bg-olive-100 border-olive-300 text-olive-700'
                : 'border-sand-300 text-stone-500 bg-white'
            }`}
          >
            <Filter size={18} />
          </button>
        </div>
        
        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white rounded-xl p-4 mb-4 border border-sand-300">
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.freeOnly}
                  onChange={(e) => setFilters({...filters, freeOnly: e.target.checked})}
                  className="w-4 h-4 rounded border-sand-300 text-olive-600 focus:ring-olive-500"
                />
                <span className="text-sm text-stone-700">{txt.freeOnly}</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.familyFriendly}
                  onChange={(e) => setFilters({...filters, familyFriendly: e.target.checked})}
                  className="w-4 h-4 rounded border-sand-300 text-olive-600 focus:ring-olive-500"
                />
                <span className="text-sm text-stone-700">{txt.familyFriendly}</span>
              </label>
              
              <button
                onClick={() => setFilters({freeOnly: false, familyFriendly: false, maxDistance: 30})}
                className="text-sm text-olive-600 hover:text-olive-700 font-medium"
              >
                {txt.clearFilters}
              </button>
            </div>
          </div>
        )}
        
        <p className="text-stone-500 text-sm">
          {events.length} {txt.eventsCount}
        </p>
      </div>

      {/* Events List */}
      <div className="px-6 py-2 max-w-4xl mx-auto space-y-4 pb-24 md:pb-8">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto" />
          </div>
        ) : displayEvents.length === 0 ? (
          <div className="text-center py-12 text-stone-500">
            <CalendarNav size={48} className="mx-auto mb-4 opacity-50" />
            <p>{txt.noEvents}</p>
          </div>
        ) : (
          displayEvents.map((event) => {
            const Icon = getEventIcon(event.category);
            const happeningNow = isHappeningNow(event);
            const isSaved = savedEvents.includes(event.id);
            
            return (
              <article 
                key={event.id} 
                className="bg-white rounded-2xl overflow-hidden border border-stone-100 shadow-sm" 
                data-testid="event-card"
              >
                <div className="flex">
                  {event.image_url && (
                    <div className="w-28 h-40 md:w-36 md:h-44 flex-shrink-0 relative">
                      <img 
                        src={event.image_url} 
                        alt={event.event_name}
                        className="w-full h-full object-cover"
                      />
                      {happeningNow && (
                        <div className="absolute top-2 left-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                          {txt.happeningNow}
                        </div>
                      )}
                      {event.partner_event && (
                        <span className="absolute bottom-2 left-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                          {txt.partner}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(event.category)}`}>
                        <Icon size={12} />
                        {event.category?.replace('_', ' ')}
                      </div>
                      <button
                        onClick={() => toggleSaveEvent(event.id)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isSaved ? 'text-amber-500' : 'text-stone-300 hover:text-amber-500'
                        }`}
                      >
                        {isSaved ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
                      </button>
                    </div>
                    
                    <h3 className="font-semibold text-stone-900 mt-2">{event.event_name}</h3>
                    <p className="text-teal-700 font-medium text-sm mt-1">
                      {event.recurring ? (
                        <span className="flex items-center gap-1">
                          <Sparkles size={12} /> {formatEventDate(event)}
                        </span>
                      ) : (
                        <>
                          {formatEventDate(event)}
                          {event.start_time && ` · ${event.start_time}`}
                        </>
                      )}
                    </p>
                    <p className="text-sm text-stone-500 flex items-center gap-1 mt-1">
                      <MapPin size={12} /> {event.town}
                      {event.distance_km !== undefined && ` · ${event.distance_km}km`}
                    </p>
                    <p className="text-sm text-stone-600 mt-2 line-clamp-2">{event.description_short}</p>
                    
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {event.free_or_paid === "free" && (
                        <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">{txt.free}</span>
                      )}
                      {event.family_friendly && (
                        <span className="text-xs px-2 py-0.5 bg-pink-50 text-pink-700 rounded-full">Family</span>
                      )}
                      {event.price_info && (
                        <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full">{event.price_info}</span>
                      )}
                    </div>

                    <div className="flex gap-2 mt-3">
                      {event.booking_url ? (
                        <a
                          href={event.booking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-teal-600 hover:bg-teal-700 text-white py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <ExternalLink size={14} />
                          {txt.bookNow}
                        </a>
                      ) : (
                        <button
                          onClick={() => handleWhatsApp(event)}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <MessageCircle size={14} />
                          {txt.contact}
                        </button>
                      )}
                      
                      {event.maps_url && (
                        <a
                          href={event.maps_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 border border-stone-200 rounded-xl text-stone-500 hover:text-teal-600 hover:border-teal-200 transition-colors"
                        >
                          <Navigation size={18} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            );
          })
        )}

        {events.length > 6 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full py-3 text-teal-700 hover:text-teal-800 font-medium text-sm flex items-center justify-center gap-2"
          >
            {showAll ? txt.showLess : txt.showMore}
            <ChevronDown size={16} className={showAll ? 'rotate-180' : ''} />
          </button>
        )}
      </div>

      {/* Ask AI Modal */}
      {showAskAI && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-stone-100 flex items-center justify-between">
              <h3 className="font-semibold text-lg">{txt.askAI}</h3>
              <button onClick={() => setShowAskAI(false)} className="p-2 text-stone-500 hover:text-stone-700">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  placeholder={txt.aiPlaceholder}
                  className="flex-1 px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleAskAI()}
                />
                <button
                  onClick={handleAskAI}
                  disabled={aiLoading || !aiQuery.trim()}
                  className="px-4 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-stone-300 text-white rounded-xl font-medium transition-colors"
                >
                  {aiLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search size={20} />}
                </button>
              </div>
              
              {aiResponse && (
                <div className="mt-4 space-y-4 max-h-[50vh] overflow-y-auto">
                  <div className="bg-teal-50 rounded-xl p-4 text-teal-900">
                    <p className="text-sm whitespace-pre-wrap">{aiResponse.suggestions}</p>
                  </div>
                  
                  {aiResponse.events?.length > 0 && (
                    <div className="space-y-2">
                      {aiResponse.events.map((event) => (
                        <div key={event.id} className="bg-stone-50 rounded-xl p-3">
                          <h4 className="font-medium text-stone-900">{event.event_name}</h4>
                          <p className="text-sm text-stone-500">{event.town} · {formatEventDate(event)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
